import { PaperAirplaneIcon, PaperClipIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FileAttachmentInput } from 'src/shared/components';
import { toastErrorIfFilesOverLimit } from 'src/shared/utils/fileSizeHelpers';

import MarkdownEditor, {
  COMPOSER_EDITOR_CLASS,
  COMPOSER_EDITOR_CONTENT_CLASS
} from 'src/shared/components/MarkdownEditor';
import { addToast, Spinner } from 'src/shared/components/ui';
import {
  ButtonV2,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
  Typography
} from 'src/shared/components/ui';

import useProductUpdateInputModal from '../../hooks/useProductUpdateInputModal';
import { useUpdateTemplate } from '../../hooks/useUpdateTemplate';
import { replaceTemplateVariables } from '../../utils/replaceTemplateVariables';
import { useAskQuestion } from '../updates-tab/question-content/hooks/useAskQuestion';
import { useClarificationFiles } from '../updates-tab/question-content/hooks/useClarificationFiles';

type FormValues = {
  question: string;
  files: any[];
  quantity: number | null;
  template: string;
  message: string;
};

const ProductUpdateInputModal: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { id: productId } = useParams();
  const askQuestionMutation = useAskQuestion();
  const clarificationFilesMutation = useClarificationFiles();
  const { isOpen, data, type, close } = useProductUpdateInputModal();
  const [shouldRender, setShouldRender] = useState(false);

  const { data: updateTemplate } = useUpdateTemplate(type ?? '');

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    control,
    reset
  } = useForm<FormValues>({
    defaultValues: {
      template: '',
      message: '',
      quantity: data?.quantity ?? null,
      files: []
    },
    mode: 'onChange'
  });

  const quantity = watch('quantity');

  const {
    fields: fileFields,
    append: appendFile,
    remove: removeFile
  } = useFieldArray({
    control,
    name: 'files'
  });

  // Handle rendering with animation delay
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    } else {
      // Wait for animation to complete before removing from DOM
      // HeroUI modal animations typically take ~200-300ms
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset({
        template: '',
        message: '',
        quantity: null,
        files: []
      });
    }
  }, [isOpen, reset]);

  // Reset form with current data when modal opens
  useEffect(() => {
    if (isOpen && data) {
      reset({
        template: '',
        message: '',
        quantity: data?.quantity ?? null,
        files: []
      });
    }
  }, [isOpen, data?.quantity, reset]);

  useEffect(() => {
    if (data && updateTemplate?.message) {
      setValue(
        'template',
        replaceTemplateVariables(updateTemplate?.message, {
          name: data?.name,
          quantity: quantity?.toString() ?? 0,
          supplier_cloak: data?.supplierCloak ?? '',
          product_quote_id: data?.productQuoteId?.toString() ?? ''
        })
      );
    }
  }, [quantity, data, setValue, updateTemplate?.message, data?.name]);

  const renderFiles = () => {
    return fileFields.map((field, index) => {
      const file = watch(`files.${index}.file`);
      const caption = watch(`files.${index}.caption`);
      const preview = watch(`files.${index}.preview`);

      return (
        <FileAttachmentInput
          key={field.id}
          fileInputProps={register(`files.${index}.file`)}
          captionInputProps={register(`files.${index}.caption`)}
          fileValue={file}
          captionValue={caption}
          preview={preview}
          onRemove={() => removeFile(index)}
        />
      );
    });
  };

  const onSuccessUpdate = () => {
    addToast({
      color: 'success',
      title: (
        <div>
          <span>
            {`Your inquiry for ${updateTemplate?.title} has been sent. We will be in touch shortly.`}
          </span>
          <br />
          <a
            target="_blank"
            href={`/products/${productId}?tab=updates`}
            rel="noreferrer"
            className="text-primary underline text-sm"
          >
            View updates
          </a>
        </div>
      )
    });
    close();
  };

  const onSaveFiles = (files: any[], response: any) => {
    if (files.length !== 0 && productId) {
      const filesBody = new FormData();

      files.forEach((file, i) => {
        filesBody.append(`files[${i}]`, file?.file);
        filesBody.append(`captions[${i}]`, file?.caption);
      });

      clarificationFilesMutation.mutate(
        {
          body: filesBody,
          id: response?.id as string,
          productId
        },
        {
          onSuccess: () => {
            onSuccessUpdate();
          }
        }
      );
    }
  };

  const onSubmit = (data: any) => {
    const withFiles = data.files?.length > 0;

    if (toastErrorIfFilesOverLimit({ files: data.files ?? [], addToast })) {
      return;
    }

    if (productId) {
      const { data: modalData } = useProductUpdateInputModal.getState();
      const address = modalData?.address;
      const contactEmail =
        address?.contact_email?.trim() ||
        address?.email?.trim() ||
        address?.contactEmail?.trim();
      const addressText = address
        ? `\n\nShipping address: ${address.name}, ${address.line_1}${address.line_2 ? `, ${address.line_2}` : ''}, ${address.city}, ${address.state} ${address.zip_code}, ${address.country}${address.contact_name ? ` (Contact: ${address.contact_name})` : ''}${address.contact_phone ? ` Phone: ${address.contact_phone}` : ''}${contactEmail ? ` Email: ${contactEmail}` : ''}`
        : '';
      const question = data?.message
        ? `${data.template}\n\n${data.message}${addressText}`
        : `${data.template}${addressText}`;
      const body = {
        question: question,
        title: updateTemplate?.title,
        product: productId,
        tracking_event_id: type
      };

      askQuestionMutation.mutate(
        { body, productId, withFiles },
        {
          onSuccess: (response) => {
            if (data.files?.length > 0) {
              onSaveFiles(data.files ?? [], response);
            } else {
              onSuccessUpdate();
            }
          }
        }
      );
    }
  };

  const handleOpenChange = (open: boolean) => {
    // Only manage this modal's state - don't affect other modals
    if (!open && isOpen) {
      close();
    }
  };

  // Don't render the modal if it shouldn't be rendered to avoid interference with other modals
  // But keep it rendered during the exit animation
  if (!shouldRender) {
    return null;
  }

  return (
    <Modal size="5xl" isOpen={isOpen} onOpenChange={handleOpenChange}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1 p-5 border-b border-gray-4">
              {updateTemplate?.title ?? 'Fetching update...'}
            </ModalHeader>
            {updateTemplate?.message ? (
              <ModalBody className="p-5 flex flex-col gap-5">
                <form
                  id="update-form"
                  className="flex flex-col gap-4"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Controller
                    name="template"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        label="Message"
                        errorMessage={errors?.template?.message as string}
                        isInvalid={!!errors?.template}
                        isDisabled
                        {...field}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                  {data?.quantity && (
                    <Controller
                      name="quantity"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Quantity"
                          type="number"
                          errorMessage={errors.quantity?.message as string}
                          isInvalid={!!errors.quantity}
                          {...field}
                          value={field.value?.toString() ?? ''}
                          onChange={(e) => {
                            const value = e.target.value;

                            field.onChange(value === '' ? null : Number(value));
                          }}
                        />
                      )}
                    />
                  )}

                  <div>
                    <Typography className="text-secondary-text mb-2">
                      Provide any other details.
                    </Typography>

                    <div className="relative flex flex-col border rounded-xl border-gray-4 bg-white">
                      <Controller
                        name="message"
                        control={control}
                        render={({ field }) => (
                          <MarkdownEditor
                            markdown={field.value}
                            onChange={field.onChange}
                            className={COMPOSER_EDITOR_CLASS}
                            contentEditableClassName={
                              COMPOSER_EDITOR_CONTENT_CLASS
                            }
                          />
                        )}
                      />

                      <div className="flex flex-wrap gap-2 mx-2 pb-2 border-t border-gray-4 pt-2 mt-2">
                        {renderFiles()}
                      </div>

                      <div className="p-2 pt-0 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-grow">
                          <input
                            type="file"
                            ref={fileInputRef}
                            multiple
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const files = e.target.files;

                              if (files && files?.length > 0) {
                                const newEntries = Array.from(files).map(
                                  (file) => ({
                                    file,
                                    caption: '',
                                    preview: file.type.startsWith('image/')
                                      ? URL.createObjectURL(file)
                                      : null
                                  })
                                );

                                appendFile(newEntries);
                                e.target.value = '';
                              }
                            }}
                          />
                          <ButtonV2
                            isDisabled={askQuestionMutation.isPending}
                            size="md"
                            isIconOnly
                            color="tertiary"
                            onPress={() => fileInputRef.current?.click()}
                          >
                            <PaperClipIcon className="size-4" />
                          </ButtonV2>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </ModalBody>
            ) : (
              <ModalBody className="p-5 flex flex-col gap-5 min-h-80 items-center justify-center">
                <Spinner />
              </ModalBody>
            )}
            <ModalFooter className="justify-end border-t border-gray-4 p-5">
              <ButtonV2
                size="md"
                color="default"
                variant="flat"
                onPress={() => onClose()}
              >
                Close
              </ButtonV2>
              <ButtonV2
                size="md"
                form="update-form"
                type="submit"
                color="primary"
                isDisabled={
                  askQuestionMutation.isPending ||
                  clarificationFilesMutation.isPending ||
                  !isValid ||
                  !updateTemplate?.message
                }
                isLoading={
                  askQuestionMutation.isPending ||
                  clarificationFilesMutation.isPending
                }
                startContent={<PaperAirplaneIcon className="size-4" />}
              >
                Send update
              </ButtonV2>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ProductUpdateInputModal;
